import { FC, useContext, createContext, PropsWithChildren, useState } from 'react';
import { noop } from 'utils/noop';

export const ORG_MEMBER = 'ORG_MEMBER';
export const ORG_ADMIN = 'ORG_ADMIN';
export const PROJECT_MEMBERSHIP = 'PROJECT_MEMBERSHIP';
export const MFA_ENABLED = 'MFA_ENABLED';
export const MFA_DISABLED = 'MFA_DISABLED';

export const emptyFilters = {
  [ORG_MEMBER]: false,
  [ORG_ADMIN]: false,
  [PROJECT_MEMBERSHIP]: new Set<number>(),
  [MFA_ENABLED]: false,
  [MFA_DISABLED]: false,
};

export type Filters = typeof emptyFilters;

export type FiltersContextState = {
  search: string;
  setSearch: (search: string) => void;
  resetSearch: () => void;
  filters: Filters;
  setFilters: (filters: Filters) => void;
  resetFilters: () => void;
  numFiltersApplied: number;
  toggleFilter: (filter: keyof Filters) => void;
  toggleProjectMembershipFilter: (projectId: number) => void;
};

export const defaultState: FiltersContextState = {
  search: '',
  setSearch: noop,
  resetSearch: noop,
  resetFilters: noop,
  filters: emptyFilters,
  setFilters: noop,
  numFiltersApplied: 0,
  toggleFilter: noop,
  toggleProjectMembershipFilter: noop,
};

export const FiltersContext = createContext<FiltersContextState>(defaultState);

export const useFiltersContext = () => useContext(FiltersContext);

export const FiltersContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>(emptyFilters);

  const resetSearch = () => {
    setSearch('');
  };

  const resetFilters = () => {
    const projectMembership = filters[PROJECT_MEMBERSHIP];
    projectMembership.clear();
    setFilters({ ...emptyFilters, [PROJECT_MEMBERSHIP]: projectMembership });
  };

  const toggleProjectMembershipFilter = (projectId: number) => {
    const set = filters[PROJECT_MEMBERSHIP];

    if (set.has(projectId)) {
      set.delete(projectId);
    } else {
      set.add(projectId);
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      [PROJECT_MEMBERSHIP]: set,
    }));
  };

  const toggleFilter = (filter: keyof Filters) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filter]: !prevFilters[filter],
    }));
  };

  const { [PROJECT_MEMBERSHIP]: projectMembership, ...booleanFilters } = filters;
  const numFiltersApplied =
    Object.values(booleanFilters).filter(Boolean).length + projectMembership.size;

  const value = {
    search,
    setSearch,
    resetSearch,
    filters,
    setFilters,
    resetFilters,
    numFiltersApplied,
    toggleProjectMembershipFilter,
    toggleFilter,
  };

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};
